import { Conversation } from "common/types";
import { useEffect, useState } from "react";
import { useAudioRecorder } from "react-audio-voice-recorder";
import { useMixpanel } from "utils/hooks/useMixPanel";
import useToggleState from "utils/hooks/useToggleState";
import { generateAudioBlobByChunks } from "./utils";

type Props = {
  onCloseAudioRecorder: (show: boolean) => void;
  requestSendAudioMessage: (
    audioBlob: Blob,
    fileName?: string,
    onSuccess?: VoidFunction,
    onFailed?: VoidFunction,
    onFinally?: VoidFunction
  ) => Promise<void>;
  selectedConversation: Conversation;
  selectedDevice: string;
};

export default function useAudioRecorderLogic(props: Props) {
  const {
    onCloseAudioRecorder,
    requestSendAudioMessage,
    selectedConversation,
    selectedDevice,
  } = props;

  const { chatInteraction, contentSent } = useMixpanel();
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [isPaused, setIsPaused] = useToggleState();
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [shouldSendAudio, setShouldSendAudio] = useToggleState();
  const [recordedBlobArray, setRecordedBlobArray] = useState<Blob[]>();

  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    recordingTime,
    mediaRecorder,
  } = useAudioRecorder({
    noiseSuppression: true,
    echoCancellation: true,
  });

  useEffect(() => {
    startRecording();
  }, []);

  const deleteAudio = () => {
    stopRecording();
    onCloseAudioRecorder(false);
    setPreviewBlob(null);
    setAudioDuration(null);
    setRecordedBlobArray(null);
  };

  const sendAudio = async (blob: Blob) => {
    requestSendAudioMessage(blob, undefined, deleteAudio, setShouldSendAudio);
    chatInteraction(selectedDevice, "message-sent", {
      conversationId: selectedConversation.id,
    });
    contentSent(selectedDevice, "audio");
  };

  const requestSendAudio = () => {
    setShouldSendAudio();
    stopRecording();
  };

  const togglePauseResumeRecording = () => {
    setIsPaused();
    setPreviewBlob(null);
    if (isPaused) {
      return startRecording();
    }

    return stopRecording();
  };

  useEffect(() => {
    if (recordingTime && recordingTime > 0) {
      setAudioDuration((prev) => {
        return ++prev;
      });
    }
  }, [recordingTime, isPaused]);

  useEffect(() => {
    if (recordingBlob) {
      setRecordedBlobArray((prev) =>
        prev ? [...prev, recordingBlob] : [recordingBlob]
      );
    }
  }, [recordingBlob]);

  useEffect(() => {
    const run = async () => {
      const audioBlob = await generateAudioBlobByChunks(recordedBlobArray);
      setPreviewBlob(audioBlob);
      shouldSendAudio && sendAudio(audioBlob);
    };

    if (recordedBlobArray) {
      run();
    }
  }, [recordedBlobArray]);

  return {
    deleteAudio,
    mediaRecorder,
    togglePauseResumeRecording,
    isRecording,
    isPaused,
    recordingTime,
    previewBlob,
    audioDuration,
    requestSendAudio,
    shouldSendAudio,
  };
}
